import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, CircleNotch } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import {
  EMPTY_SERVICE_BASELINE,
  loadServiceSavedBaseline,
  resolveServiceSavePrice,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";
import { useServicePriceLimits } from "@/hooks/useServicePriceLimits";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import { toast } from "@/lib/toast";
import { buildServicePayload, type ServicePayload } from "@/components/therapist/services/service-form-payload";

import { ServiceForm, emptyForm } from "@/components/therapist/services/ServiceForm";
import { ServicesList } from "@/components/therapist/services/ServicesList";

import type { ServiceFormData } from "@/components/therapist/services/ServiceForm";
import type { TherapistServiceItem } from "@/types";

export const Route = createFileRoute("/dashboard/therapist/services")({
  component: TherapistServices,
});

type Service = TherapistServiceItem;

function TherapistServices() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pricingConfig = usePricingConfig();
  const servicePriceLimits = useServicePriceLimits();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ServiceFormData>({ ...emptyForm });
  // Baseline ucitane cijene: cijena bez PDV-a upisana u polje + SIROVI spremljeni bruto.
  // Cuva se odvojeno od `form`, jer je jedini nacin da se prepozna netaknuto polje:
  // bez PDV-a -> bruto -> bez PDV-a nije idempotentno, pa bi preracun na svakom spremanju pomjerio
  // cijenu za paru i (kad je stara cijena van danasnjeg raspona) trajno onemogucio
  // spremanje makar i same izmjene naziva.
  const [savedBaseline, setSavedBaseline] = useState<ServiceSavedBaseline>(EMPTY_SERVICE_BASELINE);

  // Get therapist profile ID from /api/therapist/me
  const { data: meData } = useQuery({
    queryKey: ["therapist", "profile", "me"],
    queryFn: () => apiGet<{ id: string }>("/api/therapist/me", t("therapist.services.errorLoadingProfile")),
  });
  const therapistId = meData?.id;

  const { data: servicesEnv, isLoading } = useQuery<{ services: Service[]; resolvedCountry: string | null }>({
    queryKey: ["therapist", "services", therapistId],
    queryFn: () => apiGet<{ services: Service[]; resolvedCountry: string | null }>(`/api/services?therapistId=${therapistId}&all=1`, t("therapist.services.errorLoading")),
    enabled: !!therapistId,
  });
  const services = servicesEnv?.services ?? [];

  const invalidateServices = () =>
    queryClient.invalidateQueries({ queryKey: ["therapist", "services", therapistId] });

  const createMutation = useMutation({
    mutationFn: (data: ServicePayload) =>
      apiSend("/api/services", "POST", { ...data, therapistId }, t("therapist.services.errorCreating")),
    onSuccess: () => {
      invalidateServices();
      resetForm();
      toast.success(t("therapist.services.saveSuccess"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Service> }) =>
      apiSend(`/api/services/${id}`, "PATCH", data, t("therapist.services.errorUpdating")),
    onSuccess: () => {
      invalidateServices();
      resetForm();
      toast.success(t("therapist.services.saveSuccess"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(`/api/services/${id}`, "DELETE", undefined, t("therapist.services.errorDeleting")),
    onSuccess: () => {
      invalidateServices();
      toast.success(t("therapist.services.deleteSuccess"));
    },
  });

  const resetForm = () => {
    setForm({ ...emptyForm });
    setSavedBaseline(EMPTY_SERVICE_BASELINE);
    setEditingId(null);
    setShowForm(false);
  };

  // Spremljena cijena je bruto; polje prikazuje cijenu bez PDV-a, pa se pri ucitavanju
  // konvertuje i uz njega se pamti sirovi bruto (vidi loadServiceSavedBaseline).
  const baselineFor = (service: Service): ServiceSavedBaseline =>
    pricingConfig.config
      ? loadServiceSavedBaseline(service.listPrice ?? null, pricingConfig.config)
      : EMPTY_SERVICE_BASELINE;

  const startEdit = (service: Service) => {
    const baseline = baselineFor(service);
    setForm({
      name: service.name,
      description: service.description,
      durationMinutes: service.durationMinutes,
      price: baseline.base,
      modality: (service.modality as ServiceFormData["modality"]) ?? "online",
      maxMembers: service.maxMembers ?? null,
    });
    setSavedBaseline(baseline);
    setEditingId(service.id);
    setShowForm(true);
  };

  const startDuplicate = (service: Service) => {
    const baseline = baselineFor(service);
    setForm({
      name: `${service.name} ${t("therapist.services.copySuffix")}`,
      description: service.description,
      durationMinutes: service.durationMinutes,
      price: baseline.base,
      modality: (service.modality as ServiceFormData["modality"]) ?? "online",
      maxMembers: service.maxMembers ?? null,
    });
    // Duplikat je NOVA usluga: nema spremljenog bruta koji bi se grandfatherovao, pa
    // cijena mora proci kroz redovnu konverziju i provjeru raspona.
    setSavedBaseline(EMPTY_SERVICE_BASELINE);
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!pricingConfig.config) {
      toast.error("Cijene se još učitavaju, pokušajte ponovo za par sekundi.");
      return;
    }
    // Ista odluka koju koristi i onboarding: netaknuta cijena se sprema doslovno kako je
    // ucitana (i prolazi i kad je van trenutnog raspona, kao na serveru), promijenjena se
    // konvertuje u bruto i provjeri.
    const resolution = resolveServiceSavePrice(
      form.price ?? 0,
      savedBaseline,
      servicePriceLimits,
      pricingConfig.config,
    );
    if ("error" in resolution) {
      toast.error(resolution.error);
      return;
    }
    // Ista shema kao na serveru; prazan opis dobija naziv usluge (kao u onboardingu).
    const built = buildServicePayload(form, resolution.price);
    if ("error" in built) {
      toast.error(built.error);
      return;
    }
    const { data } = built;
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleActive = (service: Service) => {
    updateMutation.mutate({
      id: service.id,
      data: { isActive: service.isActive ? 0 : 1 },
    });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-6xl space-y-8 pb-14">
      {/* Page heading */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("therapist.services.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("therapist.services.subtitle")}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="group inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/20 transition-all hover:shadow-md hover:shadow-primary/30 hover:brightness-110 active:scale-[0.98]"
          >
            <Plus className="size-4 transition-transform group-hover:rotate-90" weight="bold" />
            {t("therapist.services.newService")}
          </button>
        )}
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <ServiceForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          onCancel={resetForm}
          savedBaseline={savedBaseline}
        />
      )}

      {/* Services list — hidden while the create/edit form is open */}
      {!showForm && (
        <ServicesList
          services={services}
          editingId={editingId}
          onEdit={startEdit}
          onDuplicate={startDuplicate}
          onDelete={(id) => deleteMutation.mutate(id)}
          onToggleActive={toggleActive}
          onCreateNew={() => setShowForm(true)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
